import { Loader2, ThumbsDown, ThumbsUp } from 'lucide-react';

import { Button } from '@apps/web/components/ui/button';
import { cn } from '@apps/web/lib/utils';
import { type MessageRatingLabel } from '@libraries/typescript/api_client';

type MessageRatingButtonsProps = {
    readonly selectedLabel: MessageRatingLabel | null;
    readonly isSaving: boolean;
    readonly isOperationPending: boolean;
    readonly onLabelClick: (label: MessageRatingLabel) => Promise<void>;
    readonly disabled?: boolean;
    readonly className?: string;
};

export function MessageRatingButtons({
    selectedLabel,
    isSaving,
    isOperationPending,
    onLabelClick,
    disabled = false,
    className,
}: MessageRatingButtonsProps) {
    const isDisabled = disabled || isSaving || isOperationPending;

    return (
        <div className={cn('flex gap-2', className)}>
            <Button
                size="sm"
                variant="ghost"
                aria-label="Rate message as good"
                onClick={() => onLabelClick('good')}
                disabled={isDisabled}
                className={cn(
                    'h-7 px-2 text-xs',
                    selectedLabel === 'good' &&
                        'bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-700',
                )}
            >
                {isSaving && selectedLabel === 'good' ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                    <ThumbsUp
                        className={cn(
                            'h-3 w-3',
                            selectedLabel === 'good' && 'fill-current',
                        )}
                    />
                )}
            </Button>
            <Button
                size="sm"
                variant="ghost"
                aria-label="Rate message as needs work"
                onClick={() => onLabelClick('bad')}
                disabled={isDisabled}
                className={cn(
                    'h-7 px-2 text-xs',
                    selectedLabel === 'bad' &&
                        'bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700',
                )}
            >
                {isSaving && selectedLabel === 'bad' ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                    <ThumbsDown
                        className={cn(
                            'h-3 w-3',
                            selectedLabel === 'bad' && 'fill-current',
                        )}
                    />
                )}
            </Button>
        </div>
    );
}
